import { useEffect, useState } from 'react';
import type { DashboardGateway } from '../application/dashboardGateway';
import type { AuthenticatedIdentity } from '../domain/access';
import type { DashboardSummary } from '../domain/dashboard';
import styles from './DashboardScreen.module.css';

interface DashboardScreenProps {
  readonly gateway: DashboardGateway;
  readonly navigateToAccess?: () => void;
}

type DashboardState =
  | { readonly kind: 'verifying' }
  | { readonly kind: 'loading'; readonly identity: AuthenticatedIdentity }
  | { readonly kind: 'ready'; readonly identity: AuthenticatedIdentity; readonly summary: DashboardSummary }
  | { readonly kind: 'error'; readonly identity: AuthenticatedIdentity; readonly message: string };

const modules = [
  {
    href: '/inventory',
    title: 'WorkSpaces',
    copy: 'Inventario, autorizaciones y colaboración por propiedad.',
    className: styles.workspaces,
  },
  {
    href: '/network',
    title: 'Network',
    copy: 'Conecta con profesionales, sigue Agencies y construye relaciones verificadas.',
    className: styles.network,
  },
  {
    href: '/agency',
    title: 'Agency',
    copy: 'Trayectoria, perfil profesional e inventario visible.',
    className: styles.agency,
  },
  {
    href: '/business-card',
    title: 'Tarjeta profesional',
    copy: 'Identidad profesional, contacto directo y acceso al inventario autorizado.',
    className: styles.businessCard,
  },
] as const;

function defaultNavigateToAccess(): void {
  window.location.replace('/');
}

export function DashboardScreen({
  gateway,
  navigateToAccess = defaultNavigateToAccess,
}: DashboardScreenProps): React.JSX.Element {
  const [state, setState] = useState<DashboardState>({ kind: 'verifying' });
  const [signingOut, setSigningOut] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    void gateway.resolveCurrentIdentity(controller.signal).then(async (identityResult) => {
      if (!active) return;
      if (!identityResult.ok) {
        navigateToAccess();
        return;
      }

      setState({ kind: 'loading', identity: identityResult.identity });
      const summaryResult = await gateway.loadDashboardSummary(controller.signal);
      if (!summaryResult.ok) {
        setState({ kind: 'error', identity: identityResult.identity, message: summaryResult.message });
        return;
      }

      setState({ kind: 'ready', identity: identityResult.identity, summary: summaryResult.summary });
    }).catch((error: unknown) => {
      if (!controller.signal.aborted && !(error instanceof DOMException && error.name === 'AbortError')) {
        navigateToAccess();
      }
    });

    return () => {
      active = false;
      controller.abort();
    };
  }, [gateway, navigateToAccess]);

  async function handleSignOut(): Promise<void> {
    setSigningOut(true);
    try {
      await gateway.signOut();
    } finally {
      navigateToAccess();
    }
  }

  const identity = state.kind === 'verifying' ? null : state.identity;
  const summary = state.kind === 'ready' ? state.summary : null;

  return (
    <main className={styles.shell}>
      <header className={styles.appBar}>
        <a className={styles.brand} href="/dashboard" aria-label="Alpha MLS Inicio">
          <strong>Alpha•MLS</strong><span>• GLOBAL</span>
        </a>

        <div className={styles.appActions}>
          <a className={styles.homeButton} href="/dashboard" aria-label="Inicio"><span aria-hidden="true">⌂</span> Inicio</a>
          <div className={styles.role}>
            <span>ROL ACTIVO</span>
            <strong>{identity?.actor === 'founder' ? 'Fundador · operación global' : 'Perfil autorizado'}</strong>
          </div>
          <button className={styles.menuButton} type="button" aria-label="Menú principal">☰</button>
        </div>
      </header>

      <section className={styles.hero}>
        <div>
          <p className={styles.eyebrow}>ALPHA•MLS GLOBAL</p>
          <h1>{identity === null ? 'Verificando acceso…' : <>Te damos la bienvenida, <span>{identity.displayName}</span>.</>}</h1>
        </div>
        <div className={styles.roleAside}>
          <span>ROL ACTIVO</span>
          <strong>{identity?.actor === 'founder' ? 'Fundador · operación global' : 'Perfil autorizado'}</strong>
        </div>
      </section>

      {state.kind === 'verifying' || state.kind === 'loading' ? (
        <div className={styles.status} role="status">Protegiendo la sesión y preparando tus módulos…</div>
      ) : null}

      {state.kind === 'error' ? (
        <div className={styles.status} role="alert">
          <strong>La identidad está verificada.</strong>
          <span>{state.message}</span>
        </div>
      ) : null}

      <section className={styles.moduleGrid} aria-label="Módulos de Alpha MLS">
        {modules.map((module) => (
          <a className={`${styles.moduleCard ?? ''} ${module.className ?? ''}`} href={module.href} key={module.title}>
            <span className={styles.moduleShade} aria-hidden="true" />
            <div className={styles.moduleCopy}>
              <h2>{module.title}</h2>
              <p>{module.copy}</p>
            </div>
          </a>
        ))}
      </section>

      {summary !== null ? (
        <section className={styles.quietSummary} aria-label="Estado operativo">
          <span>{summary.properties.toLocaleString('es-MX')} propiedades</span>
          <span>{summary.agents.toLocaleString('es-MX')} profesionales</span>
          <span>{summary.conversations.toLocaleString('es-MX')} conversaciones</span>
        </section>
      ) : null}

      <footer className={styles.footer}>
        <span>Alpha•MLS</span>
        <button type="button" disabled={identity === null || signingOut} onClick={() => void handleSignOut()}>
          {signingOut ? 'Cerrando…' : 'Cerrar sesión'}
        </button>
      </footer>
    </main>
  );
}
